export const DWELL_BIN_SIZE_METERS = 300;
export const DWELL_CHECKIN_MINUTES = 45;
export const DWELL_MAX_GAP_MINUTES = 30;

const degreesToRadians = (value) => (value * Math.PI) / 180;

const metersToLatDegrees = (meters) => meters / 111320;
const metersToLonDegrees = (meters, lat) => meters / (111320 * Math.cos(degreesToRadians(lat)) || 1);

const normalizeCoords = (lat, lon) => {
  const numericLat = Number(lat);
  const numericLon = Number(lon);
  if (!Number.isFinite(numericLat) || !Number.isFinite(numericLon)) return null;

  const normalized = numericLat < -90 || numericLat > 90
    ? [numericLon, numericLat]
    : [numericLat, numericLon];

  const [finalLat, finalLon] = normalized;
  if (finalLat < -90 || finalLat > 90 || finalLon < -180 || finalLon > 180) return null;
  return { lat: finalLat, lon: finalLon };
};

const binKeyFor = (lat, lon, binSizeMeters) => {
  const latStep = metersToLatDegrees(binSizeMeters);
  const lonStep = metersToLonDegrees(binSizeMeters, lat) || latStep;
  const row = Math.round(lat / latStep);
  const col = Math.round(lon / lonStep);
  return `${row}:${col}`;
};

const parseTime = (value) => {
  if (!value) return null;
  const time = new Date(value).getTime();
  return Number.isFinite(time) ? time : null;
};

const addToBin = (bins, key, lat, lon, minutes, date) => {
  const existing = bins.get(key);
  if (!existing) {
    bins.set(key, {
      key,
      latSum: lat,
      lonSum: lon,
      count: 1,
      totalMinutes: minutes,
      firstDate: date || null,
      lastDate: date || null,
    });
    return;
  }

  existing.latSum += lat;
  existing.lonSum += lon;
  existing.count += 1;
  existing.totalMinutes += minutes;
  if (date) {
    if (!existing.firstDate || date < existing.firstDate) existing.firstDate = date;
    if (!existing.lastDate || date > existing.lastDate) existing.lastDate = date;
  }
};

/**
 * Agrega tempo de permanência (dwell) em bins geográficos de tamanho fixo.
 *
 * Modelo: para cada par de pontos consecutivos de uma mesma rota com
 * timestamp válido, o delta de tempo entre eles é atribuído ao bin do ponto
 * médio — isso é o "tempo gasto" naquela área, e não apenas uma contagem de
 * visitas. Deltas maiores que `DWELL_MAX_GAP_MINUTES` são descartados (gap de
 * GPS ou de sessão, não permanência real). Check-ins contribuem um valor fixo
 * (`DWELL_CHECKIN_MINUTES`), já que não têm duração registrada.
 */
export const aggregateDwellTime = (
  routePoints = [],
  checkins = [],
  binSizeMeters = DWELL_BIN_SIZE_METERS
) => {
  const bins = new Map();

  const points = (routePoints || [])
    .map((p) => {
      const coords = normalizeCoords(p?.lat, p?.lon);
      if (!coords) return null;
      const time = parseTime(p?.time);
      return time === null ? null : { ...coords, time, routeId: p?.routeId };
    })
    .filter(Boolean);

  for (let i = 1; i < points.length; i++) {
    const prev = points[i - 1];
    const curr = points[i];
    if (prev.routeId !== curr.routeId) continue;

    const deltaMinutes = (curr.time - prev.time) / 60000;
    if (deltaMinutes <= 0 || deltaMinutes > DWELL_MAX_GAP_MINUTES) continue;

    const midLat = (prev.lat + curr.lat) / 2;
    const midLon = (prev.lon + curr.lon) / 2;
    const key = binKeyFor(midLat, midLon, binSizeMeters);
    const isoDate = new Date(curr.time).toISOString().slice(0, 10);
    addToBin(bins, key, midLat, midLon, deltaMinutes, isoDate);
  }

  (checkins || []).forEach((checkin) => {
    const coords = normalizeCoords(checkin?.lat, checkin?.lon);
    if (!coords) return;
    const key = binKeyFor(coords.lat, coords.lon, binSizeMeters);
    const isoDate = checkin?.date ? String(checkin.date).slice(0, 10) : null;
    addToBin(bins, key, coords.lat, coords.lon, DWELL_CHECKIN_MINUTES, isoDate);
  });

  return [...bins.values()]
    .map((bin) => ({
      lat: Number((bin.latSum / bin.count).toFixed(6)),
      lon: Number((bin.lonSum / bin.count).toFixed(6)),
      totalMinutes: Math.round(bin.totalMinutes),
      count: bin.count,
      firstDate: bin.firstDate,
      lastDate: bin.lastDate,
    }))
    .sort((a, b) => b.totalMinutes - a.totalMinutes);
};

const ROUTE_DATA_KEYS = ['health', 'moves', 'googleMaps'];

export const buildDwellRoutePoints = (data = {}) => {
  const points = [];

  ROUTE_DATA_KEYS.forEach((dataKey) => {
    (data[dataKey] || []).forEach((route, routeIndex) => {
      const routePoints = Array.isArray(route) ? route : [route];
      const routeId = `${dataKey}-${routeIndex}`;
      routePoints.forEach((point) => {
        if (!point) return;
        points.push({
          lat: point.lat ?? point.latitude,
          lon: point.lon ?? point.longitude,
          time: point.time || point.date,
          routeId,
        });
      });
    });
  });

  return points;
};
